import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Map, AlertCircle, Pause, PartyPopper } from 'lucide-react';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import CircularProgress from '@/components/common/CircularProgress';
import { EmptyState } from '@/components/common/EmptyState';
import { PsychologistMetrics } from '../../../../hooks/api/psychologist/metrics';
import { getAcceptedResourcesLocally, getAcceptedResources, acceptJourneyResource } from '../../../../hooks/api/psychologist/journeyTracking';
import apiCoreAdapter from '../../../../hooks/apiCoreAdapter';
import type { JourneyStage } from '../../../../types/journey';
import { getJourneyResourcesForStage } from './journeyContent';
import { JourneyResourceAccordion } from './JourneyResourceAccordion';
import { JourneyBot } from './JourneyBot';
import { JourneyGlossaryContext } from './JourneyGlossaryContext';
import { JourneyNavigationContext } from './JourneyNavigationContext';
import { JOURNEY_GLOSSARY } from './journeyGlossary';
import { GlossaryTermPanel } from './GlossaryTermPanel';
import { activityTracker } from '../../../../utils/activityTracker';

interface Props {
  metrics: PsychologistMetrics | null;
  loading: boolean;
  /** El fetch de métricas falló — distingue "todavía no tuvo su primera sesión" (metrics=null
   *  tras cargar bien) de "no pudimos determinar la etapa" (metrics=null por error de red). */
  metricsError?: boolean;
  psychologistId: string;
  isDischargeStatus: boolean;
  /** Recién graduada de NZ a Plantilla, todavía sin confirmar el popup de egreso. */
  isGraduated?: boolean;
  /** Cambia a la sub-tab "Liquidación" — usado por contenido que enlaza a esa sección. */
  onGoToLiquidacion?: () => void;
}

function egresoAckKey(psychologistId: string) {
  return `psi-journey-egreso-ack-${psychologistId}`;
}

const proc = (n: number) => `${n} proceso${n === 1 ? '' : 's'} activo${n === 1 ? '' : 's'}`;
const ASSIGN_NOTE = 'siempre que tengas disponibilidad y ningún bloqueo';

// La etapa NO viene de ningún endpoint de journey — se infiere del límite interno de procesos
// (`process_limit`), que ya sube en los mismos umbrales (5 → 10 → 15/20) que describe
// `MetricsAsignacionNTab`. Es la misma data que Psi Platform ya usa en el resto de la app.
// Etapa 3 es la última — el árbol de decisión (spec Parte 5) termina en "Etapa 3 → fin del
// período inicial", no hay Etapa 4.
function computeJourneyStage(metrics: PsychologistMetrics | null): {
  stage: JourneyStage;
  activeProcesses: number;
  processLimit: number;
} {
  const activeProcesses = metrics?.active_processes ?? 0;
  const inactiveProcesses = metrics?.inactive_processes ?? 0;
  const processLimit = metrics?.process_limit ?? 0;
  const everHadAProcess = activeProcesses + inactiveProcesses > 0;

  let stage: JourneyStage;
  if (!everHadAProcess) stage = 0;
  else if (processLimit <= 5) stage = 1;
  else if (processLimit <= 10) stage = 2;
  else stage = 3;

  return { stage, activeProcesses, processLimit };
}

function getStageCopy(stage: JourneyStage, activeProcesses: number, processLimit: number): { title: string; subtitle: string } {
  switch (stage) {
    case 1:
      return {
        title: 'Etapa 1 — Primera sesión completada',
        subtitle: `Te asignaremos, ${ASSIGN_NOTE}, hasta haber completado un total de ${processLimit || 5} pacientes. Ahí vamos a revisar cómo viene todo.`,
      };
    case 2:
      return {
        title: 'Etapa 2 — Seguimos avanzando',
        subtitle: `Tenés ${proc(activeProcesses)}. Te asignaremos, ${ASSIGN_NOTE}, hasta haber completado un total de ${processLimit || 10} pacientes, y ahí revisaremos cómo viene todo.`,
      };
    default:
      return {
        title: 'Etapa 3 — Última etapa antes de terminar tu período inicial',
        subtitle: `Tenés ${proc(activeProcesses)}. Te asignaremos, ${ASSIGN_NOTE}, hasta haber completado un total de ${processLimit || 15} pacientes — ¡ya casi llegás!`,
      };
  }
}

// Copy del popup de bienvenida a etapa nueva — hardcodeado (no hay panel admin de contenido).
const STAGE_POPUP: Record<number, { emoji: string; title: string; body: string }> = {
  1: { emoji: '🎉', title: '¡Completaste tu primera sesión!', body: 'Desbloqueamos los recursos esenciales para que tus próximas sesiones fluyan bien. Necesitamos que los leas y los aceptes antes de continuar.' },
  2: { emoji: '🎉', title: '¡Avanzaste a la siguiente etapa!', body: 'Desbloqueamos nuevos recursos importantes que necesitamos que leas.' },
  3: { emoji: '🎉', title: '¡Estás en la última etapa!', body: 'Hay información nueva sobre comunicaciones, canales, pagos, PSI+ y más que necesitamos que conozcas antes de terminar tu período inicial.' },
};

function popupStorageKey(psychologistId: string, stage: number) {
  return `psi-journey-stage-popup-seen-${psychologistId}-${stage}`;
}

export const MetricsCaminoInicialTab: React.FC<Props> = ({ metrics, loading, metricsError, psychologistId, isDischargeStatus, isGraduated, onGoToLiquidacion }) => {
  const computed = useMemo(() => computeJourneyStage(metrics), [metrics]);
  const rawStage = computed.stage;
  const { activeProcesses, processLimit } = computed;

  const [acceptedKeys, setAcceptedKeys] = useState<Set<string>>(() => getAcceptedResourcesLocally(psychologistId));
  const [acceptingKey, setAcceptingKey] = useState<string | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [autoOpenKey, setAutoOpenKey] = useState<string | null>(null);
  const [egresoPopupOpen, setEgresoPopupOpen] = useState(false);
  const [totalAssigned, setTotalAssigned] = useState<number | null>(null);
  const [glossaryTermKey, setGlossaryTermKey] = useState<string | null>(null);

  // No se muestra una etapa nueva hasta haber aceptado el 100% de los recursos de la etapa
  // actualmente mostrada, aunque `process_limit` (rawStage) ya refleje que el árbol de decisión
  // avanzó.
  const stage = useMemo(() => {
    if (rawStage === 0) return 0 as JourneyStage;
    for (let s = 0; s < rawStage; s++) {
      const stageResources = getJourneyResourcesForStage(s);
      if (!stageResources.every((r) => acceptedKeys.has(r.resource_key))) return s as JourneyStage;
    }
    return rawStage;
  }, [rawStage, acceptedKeys]);
  const isGatedBehindComputedStage = stage < rawStage;
  const resources = useMemo(() => getJourneyResourcesForStage(stage), [stage]);

  const confirmEgreso = () => {
    localStorage.setItem(egresoAckKey(psychologistId), 'true');
    setEgresoPopupOpen(false);
    // Recarga forzada (no navigate de react-router): así se vuelve a pedir todo desde cero
    // (perfil, ranking, métricas) y "Tu estadía en Psi Mammoliti" refleja el ranking ya
    // actualizado, en vez de quedar con datos viejos en memoria.
    window.location.href = '/home';
  };

  useEffect(() => {
    setAcceptedKeys(getAcceptedResourcesLocally(psychologistId));
    let cancelled = false;
    getAcceptedResources(psychologistId).then((keys) => { if (!cancelled) setAcceptedKeys(keys); });
    return () => { cancelled = true; };
  }, [psychologistId]);

  // Etapa 0 gateada (ya completó su primera sesión pero todavía no aceptó el recurso de esta
  // etapa): auto-abrimos ese recurso para que quede evidente qué hay que hacer, en vez de dejarlo
  // colapsado más abajo sin ninguna pista.
  useEffect(() => {
    if (stage !== 0 || !isGatedBehindComputedStage) return;
    const firstPending = resources.find((r) => !acceptedKeys.has(r.resource_key));
    if (firstPending) setAutoOpenKey(firstPending.resource_key);
  }, [stage, isGatedBehindComputedStage, resources, acceptedKeys]);

  // "Asignado" = proceso con al menos 1 sesión real con esta psicóloga. Se cuenta con
  // `sessions_non_cancelled` (liquidadas/activas/incompletas/free, sin canceladas). Ojo: NO sirve
  // negar el bucket 'withoutsession' — ese bucket significa "tuvo sesiones liquidadas y ninguna
  // próxima", no "nunca tuvo sesión", así que negarlo contaba como asignados a los procesos que
  // todavía no tuvieron ninguna sesión (leads con horarios ofrecidos, solo canceladas, etc.).
  useEffect(() => {
    let cancelled = false;
    apiCoreAdapter.patientsInformation({ page: 1, per_page: 800 })
      .then((resp) => {
        if (cancelled) return;
        const list = Array.isArray(resp?.results) ? resp.results : [];
        const withActivation = list.filter((r) => (r.sessions_non_cancelled ?? 0) > 0);
        setTotalAssigned(withActivation.length);
      })
      .catch(() => { if (!cancelled) setTotalAssigned(null); });
    return () => { cancelled = true; };
  }, [psychologistId]);

  // Si el tour de bienvenida de Perfil (PsychologistProfile) todavía no terminó, no superponemos
  // este popup encima — esperamos a que termine ('psi-profile-tour-finished') y a que la
  // psicóloga haga scroll antes de abrirlo. Si el tour ya había terminado antes, se abre como
  // siempre, sin esperar nada.
  useEffect(() => {
    if (isDischargeStatus || stage === 0) return;
    const key = popupStorageKey(psychologistId, stage);
    if (localStorage.getItem(key) === 'true') return;

    if (localStorage.getItem('psi-profile-tour-done') !== '1') {
      let waitingForScroll = false;
      const openAfterScroll = () => {
        window.removeEventListener('scroll', openAfterScroll, true);
        setPopupOpen(true);
      };
      const onTourFinished = () => {
        window.removeEventListener('psi-profile-tour-finished', onTourFinished);
        if (!waitingForScroll) {
          waitingForScroll = true;
          window.addEventListener('scroll', openAfterScroll, true);
        }
      };
      window.addEventListener('psi-profile-tour-finished', onTourFinished);
      return () => {
        window.removeEventListener('psi-profile-tour-finished', onTourFinished);
        window.removeEventListener('scroll', openAfterScroll, true);
      };
    }

    setPopupOpen(true);
  }, [stage, isDischargeStatus, psychologistId]);

  const handleAccept = useCallback(async (resourceKey: string) => {
    setAcceptingKey(resourceKey);
    setAcceptedKeys((prev) => new Set(prev).add(resourceKey));
    await acceptJourneyResource(psychologistId, stage, resourceKey);
    // Métricas de Actividad (funnel camino_inicial): aceptó un recurso (solo la clave genérica, no cuál).
    activityTracker.trackAction('journey_resource_accepted');
    setAcceptingKey(null);
  }, [psychologistId, stage]);

  const closePopup = () => {
    localStorage.setItem(popupStorageKey(psychologistId, stage), 'true');
    setPopupOpen(false);
  };

  const handleSeeNewResources = () => {
    closePopup();
    const firstNew = resources.find((r) => r.isNewInStage && !acceptedKeys.has(r.resource_key));
    if (firstNew) {
      setAutoOpenKey(firstNew.resource_key);
      setTimeout(() => {
        document.getElementById(`journey-resource-${firstNew.resource_key}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <CircularProgress label="Cargando tu camino inicial..." />
      </div>
    );
  }

  if (isGraduated) {
    return (
      <>
        <div className="rounded-xl border border-green-200 bg-green-50 p-5 flex flex-col gap-3">
          <div className="flex items-center gap-2 text-green-800 font-semibold text-sm">
            <PartyPopper size={17} />
            ¡Felicitaciones, ya sos parte de la plantilla!
          </div>
          <p className="text-sm text-green-700 leading-relaxed">
            Completaste tu período inicial en Psi Mammoliti. A partir de ahora vas a tener acceso
            a toda la información habitual de tu estadía (Resumen, Desempeño, Asignaciones y
            Programa PSI+).
          </p>
          <Button onClick={() => setEgresoPopupOpen(true)} className="w-fit">
            Ir al panel principal
          </Button>
        </div>

        <Dialog open={egresoPopupOpen} onOpenChange={(open) => { if (!open) setEgresoPopupOpen(false); }}>
          <DialogContent className="p-6 sm:max-w-sm">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="text-4xl">🎉</div>
              <DialogTitle className="text-xl">¡Felicitaciones!</DialogTitle>
              <DialogDescription className="text-base">
                Tu período inicial terminó — esta pantalla ya no va a estar disponible. La próxima
                vez que entres acá vas a ver toda la información habitual, ampliada.
              </DialogDescription>
              <Button onClick={confirmEgreso} className="w-full mt-2">
                Entendido
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  if (isDischargeStatus) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-5 flex flex-col gap-3">
        <div className="flex items-center gap-2 text-red-800 font-semibold text-sm">
          <Pause size={17} />
          Asignación de nuevos pacientes pausada
        </div>
        <p className="text-sm text-red-700 leading-relaxed">
          Por el momento no vamos a asignarte nuevos pacientes. Los procesos activos que ya tenés
          en curso continúan sin cambios — podés seguir atendiendo con normalidad.
        </p>
        <div className="bg-white border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 leading-relaxed">
          <strong className="block text-red-900 mb-1">¿Qué hacer con tu agenda?</strong>
          Mantené al menos 4 horas de reprogramación configuradas para que los pacientes que ya
          tenés puedan reagendar. No hace falta que tengas horarios estándar. Si tenés dudas,
          escribinos a administracion@psimammoliti.com.
        </div>
      </div>
    );
  }

  if (metricsError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <div className="bg-red-100 rounded-2xl p-4">
          <AlertCircle size={28} className="text-red-500" />
        </div>
        <p className="text-base font-semibold text-gray-800">No pudimos cargar tu camino inicial</p>
        <p className="text-sm text-gray-500">Intentá de nuevo en unos minutos.</p>
      </div>
    );
  }

  if (stage === 0) {
    return (
      <div className="flex flex-col gap-4">
        {isGatedBehindComputedStage ? (
          <div>
            <p className="text-sm font-semibold text-gray-900">¡Ya completaste tu primera sesión!</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Para continuar a la siguiente etapa, confirmá que leíste y aceptaste el recurso de abajo 👇
            </p>
          </div>
        ) : (
          <EmptyState
            title="Todavía no completaste tu primera sesión"
            description="Acá vas a encontrar todo lo que necesitás saber para empezar bien. El contenido se desbloquea a medida que avanzás — tu primer paso es completar tu primera sesión."
            showAction={false}
          />
        )}
        <JourneyResourceAccordion
          resources={resources}
          acceptedKeys={acceptedKeys}
          acceptingKey={acceptingKey}
          onAccept={handleAccept}
          autoOpenKey={autoOpenKey}
        />
      </div>
    );
  }

  const { title, subtitle } = getStageCopy(stage, activeProcesses, processLimit);
  const totalResources = resources.length;
  const acceptedCount = resources.filter((r) => acceptedKeys.has(r.resource_key)).length;
  const progressPct = totalResources > 0 ? Math.round((acceptedCount / totalResources) * 100) : 0;
  const hasPendingNew = resources.some((r) => r.isNewInStage && !acceptedKeys.has(r.resource_key));
  const popupCopy = STAGE_POPUP[stage];
  const newResourcesForPopup = resources.filter((r) => r.isNewInStage);

  return (
    <JourneyGlossaryContext.Provider value={setGlossaryTermKey}>
    <JourneyNavigationContext.Provider value={onGoToLiquidacion ?? (() => undefined)}>
    <div className="flex flex-col gap-4">
      <div className="rounded-xl border border-violet-200 bg-violet-50 p-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-violet-200 flex items-center justify-center shrink-0">
          <Map size={17} className="text-violet-800" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-violet-950">{title}</p>
          <p className="text-sm text-violet-800 leading-relaxed mt-0.5">{subtitle}</p>
          {totalAssigned !== null && (
            <div className="mt-2 inline-block rounded-lg border border-violet-300 bg-white px-3 py-1.5 text-sm font-semibold text-violet-900">
              En total ya te hemos asignado {totalAssigned} paciente{totalAssigned === 1 ? '' : 's'}.
            </div>
          )}
          {isGatedBehindComputedStage && (
            <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 leading-relaxed">
              📬 Vas a poder ver cómo sigue tu camino apenas aceptes todos los recursos de esta página —
              mirá la barra de progreso más abajo.
            </div>
          )}
          <div className="flex gap-1.5 mt-2">
            {[1, 2, 3].map((s) => (
              <span
                key={s}
                className={`w-1.5 h-1.5 rounded-full ${
                  s < stage ? 'bg-violet-600' : s === stage ? 'bg-violet-600 ring-2 ring-violet-200' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="flex justify-between text-xs text-gray-400 mb-1.5">
          <span>Recursos aceptados</span>
          <span>{acceptedCount} de {totalResources}</span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
          <div className="h-full rounded-full bg-violet-500 transition-all" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {hasPendingNew && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex gap-2.5 items-start">
          <AlertCircle size={16} className="text-amber-700 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 leading-relaxed">
            <strong className="font-medium">Hay recursos nuevos que necesitamos que leas y aceptes.</strong>{' '}
            Están marcados con &quot;Nuevo&quot; abajo.
          </p>
        </div>
      )}

      <div>
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Recursos de esta etapa</p>
        <JourneyResourceAccordion
          resources={resources}
          acceptedKeys={acceptedKeys}
          acceptingKey={acceptingKey}
          onAccept={handleAccept}
          autoOpenKey={autoOpenKey}
        />
      </div>

      <JourneyBot stage={stage} psychologistId={psychologistId} />

      <Dialog open={popupOpen} onOpenChange={(open) => { if (!open) closePopup(); }}>
        <DialogContent className="p-6 sm:max-w-sm">
          {popupCopy && (
            <div className="flex flex-col items-center text-center gap-4">
              <div className="text-4xl">{popupCopy.emoji}</div>
              <DialogTitle className="text-xl">{popupCopy.title}</DialogTitle>
              <DialogDescription className="text-base">{popupCopy.body}</DialogDescription>
              {newResourcesForPopup.length > 0 && (
                <div className="bg-gray-50 rounded-lg px-3 py-3 text-left w-full">
                  <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1.5">Nuevos recursos desbloqueados</p>
                  {newResourcesForPopup.map((r) => (
                    <p key={r.resource_key} className="text-sm text-gray-600 py-0.5" style={{ wordSpacing: '0.2em' }}>{r.title}</p>
                  ))}
                </div>
              )}
              <Button onClick={handleSeeNewResources} className="w-full mt-2">
                Ver los nuevos recursos
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <GlossaryTermPanel
        term={glossaryTermKey ? JOURNEY_GLOSSARY[glossaryTermKey] ?? null : null}
        onClose={() => setGlossaryTermKey(null)}
      />
    </div>
    </JourneyNavigationContext.Provider>
    </JourneyGlossaryContext.Provider>
  );
};

export default MetricsCaminoInicialTab;
